// =============================================================================
// AGC: DROID FARM - Auto-Detect Fields Button Component
// AGC: Triggers automatic field boundary detection using NDVI satellite data
// AGC: Shows progress and results for review
// =============================================================================

import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Satellite, Loader2, CheckCircle, AlertCircle, MapPin, RefreshCw } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import { getBackendUrl } from '../../utils/backendUrl'

// AGC: Backend API base URL — centralized via getBackendUrl()
const API_BASE_URL = getBackendUrl()

interface AutoDetectButtonProps {
  // AGC: Area of interest for detection (bounds or GeoJSON polygon)
  aoi: {
    northEast: { lat: number; lng: number }
    southWest: { lat: number; lng: number }
  } | null
  // AGC: Callback when detection completes with results
  onDetectionComplete: (results: AutoDetectResult) => void
  // AGC: Callback when detection fails
  onError?: (error: string) => void
  // AGC: Disabled state
  disabled?: boolean
  // AGC: Custom class name
  className?: string
}

// AGC: Result structure from auto-detection
export interface AutoDetectResult {
  type: 'FeatureCollection'
  features: Array<{
    id: string
    type: 'Feature'
    geometry: {
      type: 'Polygon'
      coordinates: number[][][]
    }
    properties: {
      label: number
      areaHa: number
      areaPixels: number
      confidence: number
      centroid: { lat: number; lng: number }
    }
  }>
  metadata: {
    jobId: string
    processingTimeMs: number
    bbox: number[]
    ndviThreshold: number
    totalFieldsDetected: number
    totalAreaHa: number
  }
}

// AGC: Job status type
type JobStatus = 'idle' | 'starting' | 'fetching' | 'processing' | 'extracting' | 'complete' | 'failed'

export const AutoDetectButton: React.FC<AutoDetectButtonProps> = ({
  aoi,
  onDetectionComplete,
  onError,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation()
  const [status, setStatus] = useState<JobStatus>('idle')
  const [progress, setProgress] = useState(0)
  const [message, setMessage] = useState('')
  const [jobId, setJobId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollCleanupRef = useRef<(() => void) | null>(null)

  // AGC: Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollCleanupRef.current) {
        pollCleanupRef.current()
      }
    }
  }, [])

  // AGC: Start auto-detection job
  const startDetection = useCallback(async () => {
    if (!aoi) {
      setError(t('fields.autoDetect.noAoiError'))
      onError?.(t('fields.autoDetect.noAoiError'))
      return
    }

    setStatus('starting')
    setProgress(0)
    setMessage(t('fields.autoDetect.starting'))
    setError(null)

    try {
      // AGC: Start the detection job
      const response = await fetch(`${API_BASE_URL}/api/v1/boundaries/auto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aoi }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error?.message || 'Failed to start detection')
      }

      const { data } = await response.json()
      setJobId(data.jobId)
      
      // AGC: Cleanup previous polling if any
      if (pollCleanupRef.current) {
        pollCleanupRef.current()
      }
      
      // AGC: Poll for job status and store cleanup function
      pollCleanupRef.current = pollJobStatus(data.jobId) || null
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setStatus('failed')
      setError(errorMessage)
      onError?.(errorMessage)
      console.error('[AutoDetect] AGC: Failed to start detection:', err)
    }
  }, [aoi, onError, t])

  // AGC: Fetch detection results (defined before pollJobStatus to avoid hoisting issues)
  const fetchResults = useCallback(async (resultJobId: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/boundaries/auto/${resultJobId}/result`)
      
      if (!response.ok) {
        throw new Error('Failed to get results')
      }

      const { data } = await response.json()
      onDetectionComplete(data)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get results'
      setStatus('failed')
      setError(errorMessage)
      onError?.(errorMessage)
    }
  }, [onDetectionComplete, onError])

  // AGC: Poll job status until complete or failed
  const pollJobStatus = useCallback((jobId: string) => {
    const pollInterval = 1000 // AGC: Poll every second
    const maxAttempts = 300 // AGC: Max 5 minutes
    let pollTimeout: NodeJS.Timeout | null = null
    let isPolling = true
    let attempts = 0

    const poll = async () => {
      if (!isPolling) return // AGC: Stop if component unmounted or reset
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/boundaries/auto/${jobId}`)
        
        // AGC: Check for 404 - job expired or not found
        if (response.status === 404) {
          try {
            const errorData = await response.json()
            const errorMsg = errorData.error?.message || 'Job expired or not found. Please try again.'
            console.log(`[AutoDetect] AGC: Job ${jobId} not found:`, errorMsg)
            setStatus('failed')
            setError(errorMsg)
            onError?.(errorMsg)
          } catch {
            console.log(`[AutoDetect] AGC: Job ${jobId} not found (expired or cleared), stopping poll`)
            setStatus('failed')
            setError('Job expired or not found. Please try again.')
            onError?.('Job expired or not found. Please try again.')
          }
          isPolling = false
          return
        }
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to get job status`)
        }

        const { data } = await response.json()
        
        setProgress(data.progress)
        setMessage(data.message || '')

        // AGC: Map backend status to frontend status
        if (data.status === 'fetching') setStatus('fetching')
        else if (data.status === 'processing') setStatus('processing')
        else if (data.status === 'extracting') setStatus('extracting')
        else if (data.status === 'complete') {
          setStatus('complete')
          isPolling = false
          // AGC: Fetch results
          fetchResults(jobId)
          return
        } else if (data.status === 'failed') {
          setStatus('failed')
          setError(data.error || 'Detection failed')
          onError?.(data.error || 'Detection failed')
          isPolling = false
          return
        }

        // AGC: Continue polling
        attempts++
        if (attempts < maxAttempts && isPolling) {
          pollTimeout = setTimeout(poll, pollInterval)
        } else {
          setStatus('failed')
          setError('Detection timed out')
          onError?.('Detection timed out')
          isPolling = false
        }
      } catch (err) {
        console.error('[AutoDetect] AGC: Poll error:', err)
        attempts++
        if (attempts < maxAttempts && isPolling) {
          pollTimeout = setTimeout(poll, pollInterval * 2) // AGC: Backoff on error
        } else {
          setStatus('failed')
          setError('Connection lost')
          onError?.('Connection lost')
          isPolling = false
        }
      }
    }

    poll()
    
    // AGC: Return cleanup function
    return () => {
      isPolling = false
      if (pollTimeout) {
        clearTimeout(pollTimeout)
      }
    }
  }, [onError, fetchResults])

  // AGC: Reset to idle state
  const reset = useCallback(() => {
    // AGC: Stop polling if active
    if (pollCleanupRef.current) {
      pollCleanupRef.current()
      pollCleanupRef.current = null
    }
    setStatus('idle')
    setProgress(0)
    setMessage('')
    setJobId(null)
    setError(null)
  }, [])

  // AGC: Get status icon
  const getStatusIcon = () => {
    switch (status) {
      case 'starting':
      case 'fetching':
      case 'processing':
      case 'extracting':
        return <Loader2 className="w-5 h-5 animate-spin" />
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-green-400" />
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-400" />
      default:
        return <Satellite className="w-5 h-5" />
    }
  }

  // AGC: Get button text
  const getButtonText = () => {
    switch (status) {
      case 'starting':
        return t('fields.autoDetect.starting')
      case 'fetching':
        return t('fields.autoDetect.fetchingSatellite')
      case 'processing':
        return t('fields.autoDetect.processing')
      case 'extracting':
        return t('fields.autoDetect.extracting')
      case 'complete':
        return t('fields.autoDetect.complete')
      case 'failed':
        return t('fields.autoDetect.failed')
      default:
        return t('fields.autoDetect.button')
    }
  }

  // AGC: Check if button should be disabled
  const isDisabled = disabled || !aoi || (status !== 'idle' && status !== 'complete' && status !== 'failed')

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* AGC: Main button */}
      <button
        onClick={status === 'idle' ? startDetection : status === 'failed' || status === 'complete' ? reset : undefined}
        disabled={isDisabled}
        className={`
          flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all
          ${isDisabled 
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
            : status === 'failed'
              ? 'bg-red-600 hover:bg-red-700 text-white'
              : status === 'complete'
                ? 'bg-green-600 hover:bg-green-700 text-white'
                : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg'
          }
        `}
      >
        {getStatusIcon()}
        <span>{getButtonText()}</span>
        {status === 'failed' || status === 'complete' ? (
          <RefreshCw className="w-4 h-4 ml-1" />
        ) : null}
      </button>

      {/* AGC: Progress bar */}
      {(status !== 'idle' && status !== 'complete' && status !== 'failed') && (
        <div className="w-full">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>{message}</span>
            <span>{progress}%</span>
          </div>
          <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* AGC: Error message */}
      {error && status === 'failed' && (
        <div className="flex items-center gap-2 text-sm text-red-400 bg-red-900/30 px-3 py-2 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* AGC: Info text */}
      {status === 'idle' && (
        <p className="text-xs text-gray-500 text-center">
          {t('fields.autoDetect.description')}
        </p>
      )}
    </div>
  )
}

export default AutoDetectButton

